"use client"; // Enable client-side rendering

import React, { Suspense, useState, useEffect, useMemo, useCallback } from 'react';
// import ProductTracking from './ProductTracking';
import he from 'he'; // Decode HTML entities
import ProductDetailTabs from './ProductDetailTabs';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

const PLACEHOLDER_IMAGE = '/images/placeholder.png';

const ProductPageLayout = ({ 
  product, 
  locationsData = [], 
  queryParams = {}, 
  country = 'uae', 
  Webfeed 
}) => {
  const [activeImage, setActiveImage] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const currentcountry = useSelector((state) => state.globalslice.currentcountry);
  // Memoize derived values
  const {
    displayPrice,
    oldPrice,
    galleryImages,
    isOutOfStock,
    productInfoDetails,
    decodedDetails
  } = useMemo(() => ({
    displayPrice: product?.display_price || 'Price not available',
    oldPrice: product?.old_price,
    galleryImages: product?.images?.length ? product.images : (product?.image ? [product.image] : []),
    isOutOfStock: product?.stock?.toLowerCase() !== 'in stock' || 
                 !product?.quantity || 
                 product.quantity <= 0 || 
                 !product?.display_price || 
                 typeof product?.display_price !== "number",
    productInfoDetails: [
      ...(product?.small_desc_data || [])
        .filter(item => item.title && (item.value || item.value === 0))
        .map(item => ({
          label: item.title,
          value: item.value
        }))
    ],
    decodedDetails: product?.details ? he.decode(product.details) : ''
  }), [product]);

  // Update active image when product changes
  useEffect(() => {
    setActiveImage(galleryImages[0] || null);
    setImageError(false);
    setIsLoading(true);
  }, [product?.id, galleryImages]);

  const handleImageError = useCallback((e) => {
    if (e.currentTarget.src !== PLACEHOLDER_IMAGE) {
      e.currentTarget.src = PLACEHOLDER_IMAGE;
      setImageError(true);
    }
  }, []);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleThumbnailClick = useCallback((imgUrl) => {
    setActiveImage(imgUrl);
    setImageError(false);
    setIsLoading(true);
  }, []);

  if (!product) {
    return (
      <div className="d-flex flex-column min-vh-100 bg-white">
        <main className="flex-grow-1 container py-5 text-center">
          <div className="placeholder-glow">
            <div className="placeholder col-9 mb-4 mx-auto" style={{ height: '2rem' }}></div>
            <div className="placeholder col-6 mx-auto" style={{ height: '16rem' }}></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column min-vh-100 bg-white">

      {/* Product tracking */}
      <Suspense fallback={
        <div className="text-center p-4">
          <div className="placeholder-glow">
            <div className="placeholder col-3 mx-auto" style={{ height: '1rem' }}></div>
          </div>
        </div>
      }>
        {/* <ProductTracking 
          product={product} 
          queryParams={queryParams} 
          country={country} 
        /> */}
      </Suspense>

      <main className="flex-grow-1 container py-3 py-md-5 user-select-none">
        <div className="row" style={{ minHeight: 'calc(100vh - 200px)' }}>
          {/* Image Section */}
          <div className="col-12 col-lg-5 mb-4 mb-lg-0 position-lg-sticky" style={{ top: '1rem', height: 'fit-content' }}>
            <div className="position-relative bg-light rounded overflow-hidden shadow-sm border mb-2" style={{ aspectRatio: '1/1' }}>
              {isLoading && (
                <div className="position-absolute top-0 start-0 end-0 bottom-0 d-flex align-items-center justify-content-center bg-white bg-opacity-75">
                  <div className="loading-spinner">
                    <style>
                      {`
                        .loading-spinner {
                          width: 50px;
                          height: 50px;
                          border: 5px solid #f3f3f3;
                          border-top: 5px solid #3498db;
                          border-radius: 50%;
                          animation: spin 1s linear infinite;
                        }
                        @keyframes spin {
                          0% { transform: rotate(0deg); }
                          100% { transform: rotate(360deg); }
                        }
                      `}
                    </style>
                  </div>
                </div>
              )}
              
              {activeImage ? (
                <img
                  key={activeImage}
                  src={activeImage}
                  alt={product.name || 'Product Image'}
                  className={`img-fluid h-100 w-100 object-fit-contain transition ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                  onError={handleImageError}
                  onLoad={handleImageLoad}
                  loading="lazy"
                />
              ) : (
                <div className="text-muted">No Image Available</div>
              )}

              {isOutOfStock && (
                <div className="position-absolute top-0 start-0 end-0 bottom-0 bg-dark bg-opacity-50 d-flex align-items-center justify-content-center">
                  <span className="badge bg-danger fs-5 p-2">OUT OF STOCK</span>
                </div>
              )}
            </div>

            {/* Thumbnail Slider */}
            {galleryImages.length > 1 && (
              <div className="d-flex gap-2 overflow-auto py-1">
                {galleryImages.map((imgUrl, index) => (
                  <button
                    key={index}
                    onClick={() => handleThumbnailClick(imgUrl)}
                    className={`btn p-1 border flex-shrink-0 ${
                      activeImage === imgUrl 
                        ? ' border-primary' 
                        : ''
                    }`}
                    style={{ width: '5rem', height: '5rem' }}
                  >
                    <img
                      src={imgUrl}
                      alt={`Thumbnail ${index + 1}`}
                      className="img-fluid h-100 w-100 object-fit-contain"
                      onError={(e) => e.currentTarget.style.display = 'none'}
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info Section */}
          <div className="col-12 col-lg-7" style={{ 
            maxHeight: 'calc(100vh - 160px)', 
            overflowY: 'auto',
            scrollbarWidth: 'none',  /* Firefox */
            msOverflowStyle: 'none',  /* IE and Edge */
          }}>
            <style>
              {`
                .col-12.col-lg-7::-webkit-scrollbar {
                  display: none;  /* Chrome, Safari and Opera */
                }
              `}
            </style>
            <div className="w-100">
              <h1 className="fs-5 fs-sm-3 fw-semibold text-dark mb-0 border-bottom pb-2">
                {product.name || 'Product Name Unavailable'}
              </h1>
              <div className="d-flex align-items-center gap-2 border-bottom mb-3">
                <p className={`fs-2 fw-bold text-primary mb-0 ${isOutOfStock ? 'text-decoration-line-through opacity-75' : ''}`}>
                 <div className='text-dark d-flex align-items-center gap-1'> 
                  <span className='fs-5 fw-semibold'>{!isOutOfStock ? currentcountry.currency : ''}</span>
                   <span className=''>{typeof displayPrice === "number" && displayPrice }</span> 
                   </div>
                </p>
                {isOutOfStock && (
                  <span className="badge bg-danger fs-5">OUT OF STOCK</span>
                )}
              </div>
              
              {/* Product Attributes */}
              {productInfoDetails.length > 0 && (
                <div className="pb-4 w-100">
                  <div className="row g-2">
                    {productInfoDetails.map((detail, index) => (
                      <div key={`${detail.label}-${index}`} className="col-4 col-md-4 col-sm-6">
                        <div className="bg-light p-2 rounded-3 h-100">
                          <div style={{fontSize: "12px"}} className="text-secondary">{detail.label}</div>
                          <div style={{fontSize: "14px"}} className="text-dark text-break">{detail.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <ProductDetailTabs 
              product={product}
              locationsData={locationsData}
              queryParams={queryParams}
              country={country}
              Webfeed={Webfeed}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

ProductPageLayout.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    display_price: PropTypes.string,
    old_price: PropTypes.string,
    stock: PropTypes.string,
    quantity: PropTypes.number,
    sku: PropTypes.string,
    brand: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.string),
    image: PropTypes.string,
    small_desc_data: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })),
    details: PropTypes.string
  }),
  locationsData: PropTypes.array,
  queryParams: PropTypes.object,
  country: PropTypes.string,
  Webfeed: PropTypes.object
};

export default ProductPageLayout;
